import { UploadFormat, UploadStatus, UploadColumnType } from '@/types/upload';

/**
 * What a workspace takes as a file: workbooks and tables (whose places are coordinate or WKT columns, or which are
 * data without places), and files with their own geometry. A shapefile comes as one .zip with its .shp, .dbf, .shx
 * and .prj.
 */
export const UPLOAD_ACCEPT = '.xlsx,.xls,.csv,.txt,.geojson,.json,.zip,.gpkg,.kml';

/** Formats whose places are columns of a table; the others carry their own geometry. */
export const TABLE_FORMATS: readonly UploadFormat[] = ['EXCEL', 'CSV'];

/** Reading a big file can take longer than the API client's usual 60 s. */
export const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

/** How often an upload that is still importing is asked for again. */
export const UPLOAD_STATUS_REFRESH_MS = 2000;

export const UPLOAD_IN_PROGRESS_STATUSES: readonly UploadStatus[] = ['QUEUED', 'PROCESSING'];

export const UPLOAD_COLUMN_TYPES: readonly UploadColumnType[] = ['TEXT', 'INTEGER', 'DECIMAL', 'BOOLEAN', 'DATE'];

export const DEFAULT_UPLOAD_CRS = 'EPSG:4326';

/** The query key of a workspace's uploads; one upload's key starts with it. */
export const uploadsKey = (workspace: string) => ['uploads', workspace] as const;

/** Badge colours per status; the words are i18n keys uploads.status_<status>. */
export const UPLOAD_STATUS_CLASSES: Record<UploadStatus, string> = {
  WAITING: 'bg-gn-warning-light text-gn-warning',
  QUEUED: 'bg-gn-blue-light text-gn-blue',
  PROCESSING: 'bg-gn-blue-light text-gn-blue',
  DONE: 'bg-gn-success-light text-gn-success',
  FAILED: 'bg-gn-danger-light text-gn-danger',
  EXPIRED: 'bg-gn-tertiary text-gn-secondary',
};

/**
 * Coordinate systems a table's coordinate columns can be in, grouped for the dropdown. The group names are i18n keys
 * (uploads.crs_group_<key>); the systems keep their EPSG names, which are the same in every language. Degrees first,
 * then the national grids of the countries GeoNexora has data for, then the UTM zones. Add a system here to offer it.
 */
export const UPLOAD_CRS_GROUPS: { key: string; options: { value: string; label: string }[] }[] = [
  { key: 'degrees', options: [{ value: DEFAULT_UPLOAD_CRS, label: 'WGS 84 (EPSG:4326)' }] },
  {
    key: 'nl',
    options: [{ value: 'EPSG:28992', label: 'Amersfoort / RD New (EPSG:28992)' }],
  },
  {
    key: 'de',
    options: [
      { value: 'EPSG:25832', label: 'ETRS89 / UTM 32N (EPSG:25832)' },
      { value: 'EPSG:25833', label: 'ETRS89 / UTM 33N (EPSG:25833)' },
      { value: 'EPSG:31467', label: 'DHDN / Gauss-Krüger 3 (EPSG:31467)' },
    ],
  },
  { key: 'uk', options: [{ value: 'EPSG:27700', label: 'OSGB36 / British National Grid (EPSG:27700)' }] },
  {
    key: 'tr',
    options: [
      { value: 'EPSG:5253', label: 'TUREF / TM27 (EPSG:5253)' },
      { value: 'EPSG:5254', label: 'TUREF / TM30 (EPSG:5254)' },
      { value: 'EPSG:5255', label: 'TUREF / TM33 (EPSG:5255)' },
      { value: 'EPSG:5256', label: 'TUREF / TM36 (EPSG:5256)' },
      { value: 'EPSG:5257', label: 'TUREF / TM39 (EPSG:5257)' },
      { value: 'EPSG:5258', label: 'TUREF / TM42 (EPSG:5258)' },
      { value: 'EPSG:5259', label: 'TUREF / TM45 (EPSG:5259)' },
      { value: 'EPSG:23035', label: 'ED50 / UTM 35N (EPSG:23035)' },
      { value: 'EPSG:23036', label: 'ED50 / UTM 36N (EPSG:23036)' },
      { value: 'EPSG:23037', label: 'ED50 / UTM 37N (EPSG:23037)' },
      { value: 'EPSG:23038', label: 'ED50 / UTM 38N (EPSG:23038)' },
    ],
  },
  { key: 'ro', options: [{ value: 'EPSG:3844', label: 'Pulkovo 1942(58) / Stereo70 (EPSG:3844)' }] },
  {
    key: 'utm',
    options: [
      { value: 'EPSG:32631', label: 'WGS 84 / UTM 31N (EPSG:32631)' },
      { value: 'EPSG:32632', label: 'WGS 84 / UTM 32N (EPSG:32632)' },
      { value: 'EPSG:32633', label: 'WGS 84 / UTM 33N (EPSG:32633)' },
      { value: 'EPSG:32635', label: 'WGS 84 / UTM 35N (EPSG:32635)' },
      { value: 'EPSG:32636', label: 'WGS 84 / UTM 36N (EPSG:32636)' },
      { value: 'EPSG:32637', label: 'WGS 84 / UTM 37N (EPSG:32637)' },
      { value: 'EPSG:32638', label: 'WGS 84 / UTM 38N (EPSG:32638)' },
    ],
  },
  { key: 'web', options: [{ value: 'EPSG:3857', label: 'WGS 84 / Pseudo-Mercator (EPSG:3857)' }] },
];

/** Every offered system, for checking a suggested one. */
export const UPLOAD_CRS_VALUES: readonly string[] = UPLOAD_CRS_GROUPS.flatMap((g) => g.options.map((o) => o.value));
